import { CartAddEvent } from "@theme/events";
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const escapeHtml = (str = "") => {
  if (typeof str !== "string") return "";
  return str.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return char;
    }
  });
};

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.bundleItems = []; // store selected products
    this.maxSlots = 3; // default bundle size (can be 3 or 5 later)
    this.bundlePrice = 0
    this.bundleDiscountPercent = 0;
  }

  connectedCallback() {
    this.initTabs();
    this.initCollections();
    this.initBundleOptions(); // 👈 new
    this.renderBundleBox();
    this.windowHistory()
    this.initSizeChartParameters()


    const toggleBtn = this.querySelector(".bundle-toggle-btn");
    const mobileBox = this.querySelector(".main-box-container.mobile");
    const mobileHeader = mobileBox?.querySelector(".toggle-header");

    if (toggleBtn && mobileBox && mobileHeader) {
      // click bottom bar
      toggleBtn.addEventListener("click", () => {
        mobileBox.parentElement.classList.add("open");
        document.body.classList.add("bundle-menu-open")
        toggleBtn.parentElement?.classList.add("bundle-open-mobile")
        // mobileBox.style.height = "100vh"; // adjust if needed
        toggleBtn.style.display = "none"; // hide bottom button
      });

      // click header inside mobile box
      mobileHeader.addEventListener("click", () => {
        if (mobileBox.parentElement.classList.contains("open")) {
          mobileBox.parentElement.classList.remove("open");
          document.body.classList.remove("bundle-menu-open")
          this.querySelector(".bundle-toggle-bar").classList.remove("bundle-open-mobile")
          // mobileBox.style.height = "0px";
          toggleBtn.style.display = "block"; // show back bottom button
        } else {
          mobileBox.parentElement.classList.add("open");
          document.body.classList.remove("bundle-menu-open")
          this.querySelector(".bundle-toggle-bar").classList.remove("bundle-open-mobile")
          // mobileBox.style.height = "100vh";
          toggleBtn.style.display = "none";
        }
      });
    }


  }

  isDrawerOpen() {
    const dialog = document.querySelector(".cart-drawer__dialog");
    return dialog?.hasAttribute("open"); // reliable in Shopify's cart drawer
  }


  closeDialog() {
    const dialog = document.querySelector(".cart-drawer__dialog");
    const cartDrawer = document.querySelector("cart-drawer-component");

    if (!dialog) return;

    const handleBackdropClick = (event) => {
      // if click target IS the dialog itself (the backdrop), not its children
      if (event.target === dialog) {
        cartDrawer?.close?.();
        dialog.removeEventListener("click", handleBackdropClick);
      }
    };

    // attach directly to the dialog
    setTimeout(() => {
      dialog.addEventListener("click", handleBackdropClick);
    }, 0);
  }


  initBundleOptions() {
    const optionBtns = this.querySelectorAll(".bundle-option");

    this.cacheOriginalBundleOptionState();

    optionBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        // Reset active state
        optionBtns.forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");

        // Update bundle config
        this.maxSlots = parseInt(btn.dataset.slots, 10);
        this.bundlePrice = parseInt(btn.dataset.bundlePrice, 10) || 0;

        if (this.bundleItems.length > this.maxSlots) {
          this.bundleItems = this.bundleItems.slice(0, this.maxSlots);
        }

        this.renderBundleBox();
      });
    });

    // ✅ Always select defaults when initializing
    this.selectDefaultBundleOptions();
  }

  selectDefaultBundleOptions() {
    // reset all
    this.querySelectorAll(".bundle-option").forEach(b => b.classList.remove("is-active"));

    // helper to activate first option
    const activateFirstOption = (selector) => {
      const firstOption = this.querySelector(`${selector} .bundle-option`);
      if (!firstOption) return false;

      firstOption.classList.add("is-active");
      this.maxSlots = parseInt(firstOption.dataset.slots, 10) || 0;
      this.bundlePrice = parseInt(firstOption.dataset.bundlePrice, 10) || 0;

      return true;
    };

    // try desktop first, fallback to mobile
    activateFirstOption(".bundle-options-desktop")
    activateFirstOption(".bundle-options-mobile");
  }

  cacheOriginalBundleOptionState() {
    this.querySelectorAll(".bundle-option").forEach(btn => {
      if (btn.dataset.originalBundlePrice === undefined) {
        btn.dataset.originalBundlePrice = btn.dataset.bundlePrice ?? "";
      }
      if (btn.dataset.originalSlots === undefined) {
        btn.dataset.originalSlots = btn.dataset.slots ?? "";
      }
      if (btn.dataset.originalBundlePriceLabel === undefined) {
        const priceLabel = btn.querySelector(".bundle-price");
        btn.dataset.originalBundlePriceLabel = priceLabel ? priceLabel.innerHTML : "";
      }
    });
  }

  restoreBundleOptionDefaults() {
    this.querySelectorAll(".bundle-option").forEach(btn => {
      const originalSlots = btn.dataset.originalSlots;
      if (originalSlots !== undefined) {
        if (originalSlots === "") {
          btn.removeAttribute("data-slots");
        } else {
          btn.dataset.slots = originalSlots;
        }
      }

      const originalPrice = btn.dataset.originalBundlePrice;
      const originalLabel = btn.dataset.originalBundlePriceLabel;
      const priceLabel = btn.querySelector(".bundle-price");

      if (originalPrice !== undefined) {
        const trimmedPrice = String(originalPrice).trim();
        const numericPrice = trimmedPrice === "" ? NaN : Number(trimmedPrice);

        const isEmptyOrZero = !trimmedPrice || (!Number.isNaN(numericPrice) && numericPrice === 0);

        if (isEmptyOrZero) {
          btn.removeAttribute("data-bundle-price");
          if (priceLabel) {
            priceLabel.remove();
          }
        } else {
          btn.dataset.bundlePrice = trimmedPrice;

          if (originalLabel !== undefined) {
            if (priceLabel) {
              priceLabel.innerHTML = originalLabel;
            } else {
              const span = document.createElement("span");
              span.className = "bundle-price";
              span.innerHTML = originalLabel;
              btn.appendChild(span);
            }
          }
        }
      } else if (priceLabel && originalLabel !== undefined) {
        priceLabel.innerHTML = originalLabel;
      }
    });
  }

  formatDiscount(amount) {
    return `${amount}%`
  }

  formatBundlePrice(amount, { includeSuffix = true } = {}) {
    const currency = window?.currencySymbol || "";
    const value = Number(amount);

    if (!Number.isFinite(value)) {
      return `${currency}0${includeSuffix ? "/-" : ""}`;
    }

    if (value === 0) {
      return `${currency}0${includeSuffix ? "/-" : ""}`;
    }

    if (window.Intl?.NumberFormat) {
      const formatted = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
      return `${currency}${formatted}${includeSuffix ? "/-" : ""}`;
    }

    const fixed = value.toFixed(2);
    const formatted = fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
    return `${currency}${formatted}${includeSuffix ? "/-" : ""}`;
  }

  parseBundlePriceInput(raw) {
    if (raw == null) return null;

    if (typeof raw === "number") {
      return Number.isFinite(raw) ? raw : null;
    }

    if (typeof raw === "object") {
      const amountLike = raw.amount ?? raw.price ?? raw.value;
      return this.parseBundlePriceInput(amountLike);
    }

    const str = String(raw).trim();
    if (!str) return null;

    if ((str.startsWith("{") && str.endsWith("}")) || (str.startsWith("[") && str.endsWith("]"))) {
      try {
        const parsed = JSON.parse(str);
        return this.parseBundlePriceInput(parsed);
      } catch (error) {
        // fall through to sanitised parsing when JSON fails
      }
    }

    const sanitised = str.replace(/[^\d.,-]/g, "").replace(/,/g, "");
    if (!sanitised) return null;

    const value = Number(sanitised);
    return Number.isFinite(value) ? value : null;
  }

  updateBundleOptionPrices({ buy3Price, buy5Price, buyDiscount }) {
    const priceMap = new Map();
    const candidates = [
      [3, buy3Price],
      [5, buy5Price]
    ];

    candidates.forEach(([slots, rawPrice]) => {
      const parsed = this.parseBundlePriceInput(rawPrice);
      if (parsed != null) {
        priceMap.set(slots, parsed);
      }
    });

    this.querySelectorAll(".bundle-option").forEach((btn) => {

      let priceLabel = btn.querySelector(".bundle-price");
      if (!priceLabel) {
        priceLabel = document.createElement("span");
        priceLabel.className = "bundle-price";
        btn.appendChild(priceLabel);
      }
      if(buyDiscount > 0) {
        priceLabel.innerHTML = this.formatDiscount(buyDiscount);
      } else {
        priceLabel.innerHTML = '';      
      }
    });

    const activeBtn = this.querySelector(".bundle-option.is-active");
    if (activeBtn) {
      const activePrice = parseFloat(activeBtn.dataset.bundlePrice);
      this.bundlePrice = Number.isFinite(activePrice) ? activePrice : 0;

      const activeSlots = parseInt(activeBtn.dataset.slots, 10);
      if (Number.isFinite(activeSlots)) {
        this.maxSlots = activeSlots;
      }

      this.renderBundleBox();
    }
  }

  initTabs() {

    const headings = this.querySelectorAll(".collection-category-item");
    const contents = this.querySelectorAll("[bundle-category]");
    const collectionDiv = this.querySelectorAll(".bundle-collection-list");
    const selectedCollectionSpan = this.querySelectorAll("[data-role='selected-collection']");
    const selectedSize = this.querySelectorAll("[data-role='selected-size']");
    const selectedColor = this.querySelectorAll("[data-role='selected-color']");

    // if bundle-type is socks and only one heading exists
    // if bundle-type is socks and only one heading exists
    const bundleType = this.closest(".bundle-type-content")?.getAttribute("bundle-type");
    if (bundleType === "socks" && headings.length === 1) {
      const onlyHeading = headings[0];
      const onlyContent = contents[0];
      const collectionList = onlyContent.querySelector(".bundle-collection-list");
      this.closest(".bundle-type-content")?.querySelector(".collection-size")?.classList.add("hidden")

      // mark heading as active + hide the heading categories UI
      onlyHeading.classList.add("is-active");
      this.querySelector(".collection-categories")?.classList.add("hidden");

      // show its content
      contents.forEach(c => c.classList.add("hidden"));
      onlyContent.classList.remove("hidden");

      // ✅ also show the collection list
      if (collectionList) {
        collectionList.classList.remove("hidden");
      }

      // simulate click so existing logic (resetting, labels, etc.) runs
      onlyHeading.click();
    }

    if (!headings.length || !contents.length) return;

    // Default: first tab content visible
    contents.forEach((c, i) => {
      c.classList.toggle("hidden", i !== 0);
    });

    // Default: show first heading text inside span
    if (selectedCollectionSpan.length && selectedSize.length && selectedColor.length) {
      selectedCollectionSpan.forEach((i, index) => {
        i.textContent = ""
        selectedSize[index].textContent = ""
        selectedColor[index].textContent = ""
      })
      
    }

    headings.forEach((heading, idx) => {
      heading.addEventListener("click", (e) => {

        // reset
        headings.forEach(h => h.classList.remove("is-active"));
        contents.forEach(c => c.classList.add("hidden"));
        // activate clicked
        heading.classList.add("is-active");
        contents[idx].classList.remove("hidden");
        collectionDiv[idx]?.classList.remove("hidden");

        // update span
        if (selectedCollectionSpan.length && selectedSize.length && selectedColor.length) {
          selectedCollectionSpan.forEach((i, index) => {
            i.textContent = ""
            selectedSize[index].textContent = ""
            selectedColor[index].textContent = ""
          })
        }

        contents[idx].querySelectorAll(".bundle-collection-item").forEach(b => {
          b.classList.remove("active", "is-disabled");
          b.removeAttribute("aria-disabled");
        });
        contents[idx].querySelectorAll("[data-role='style-note']").forEach(noteEl => {
          noteEl.classList.add("hidden");
          const noteText = noteEl.querySelector("[data-role='style-note-text']");
          if (noteText) noteText.textContent = "";
        });
        contents[idx].querySelectorAll(".size-btn").forEach(b => b.classList.remove("is-active"));
        contents[idx].querySelectorAll(".color-btn").forEach(b => b.classList.remove("is-active"));
        contents[idx].querySelectorAll(".size-selector").forEach(b => b.classList.add("hidden"));
        contents[idx].querySelectorAll(".bundle-size-chart").forEach(b => b.classList.add("hidden"));
        contents[idx].querySelectorAll(".color-selector").forEach(b => b.classList.add("hidden"));
        contents[idx].querySelectorAll(".bundle-product").forEach(b => b.classList.add("hidden"));

        this.sizeChart()
      });
    });
  }

  initCollections() {
    this.querySelectorAll(".bundle-collection-item").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();

        if (link.classList.contains("is-disabled") && !link.classList.contains("active")) {
          return;
        }

        this.sizeChart(e)

        const item = link.closest(".bundle-collection-item");
        const category = item.closest("[bundle-category]");

        // reset active state for all collection items in this category
        item.closest(".bundle-collection-list")
          .querySelectorAll(".bundle-collection-item")
          .forEach(el => el.classList.remove("active"));
        item.classList.add("active");

         item.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });

        // --- labels inside this category
        const styleLabelWrap = category.querySelector(".collection-style");
        const styleLabel = category.querySelector("[data-role='selected-collection']");
        const sizeLabel = category.querySelector("[data-role='selected-size']");
        const colorLabel = category.querySelector("[data-role='selected-color']");
        const sizeSelector = category.querySelector(".size-selector");
        const sizeChart = category.querySelector(".bundle-size-chart");

        const colorSelector = category.querySelector(".color-selector");
        const productsWrap = category.querySelector(".bundle-products-wrap");
        const styleNote = category.querySelector("[data-role='style-note']");
        const styleNoteText = category.querySelector("[data-role='style-note-text']");
        const styleNoteContinue = category.querySelector("[data-role='style-note-continue']");

        // which collection clicked
        const handle = item.dataset.collectionHandle;
        const collectionName = item.dataset.collectionTitle || item.querySelector("h4")?.innerText || "";
        const productsGroup = productsWrap.querySelector(`.bundle-products[data-collection="${handle}"]`);
        this.bundleDiscountPercent = item.dataset.collectionDiscount;
        this.updateBundleOptionPrices({
          buy3Price: item.dataset.bundleCollectionPriceBuy3,
          buy5Price: item.dataset.bundleCollectionPriceBuy5,
          buyDiscount: item.dataset.collectionDiscount,
        });
        // update "Choose your style"
        if (styleLabel) styleLabel.textContent = collectionName;
        if (styleLabelWrap) styleLabelWrap.classList.remove("hidden");

        if (styleNoteContinue && !styleNoteContinue._bound) {
          styleNoteContinue.addEventListener("click", () => {
            this.resetBuilder();
            this.querySelectorAll(".collection-category-item").forEach(tab => {
              tab.classList.remove("is-disabled");
              tab.removeAttribute("aria-disabled");
            });
            if (styleLabel) styleLabel.textContent = "";
            if (sizeLabel) sizeLabel.textContent = "";
            if (colorLabel) colorLabel.textContent = "";
            category.querySelectorAll(".size-btn").forEach(b => b.classList.remove("is-active"));
            category.querySelectorAll(".color-btn").forEach(b => b.classList.remove("is-active"));
            category.querySelectorAll(".size-selector").forEach(el => el.classList.add("hidden"));
            category.querySelectorAll(".bundle-size-chart").forEach(el => el.classList.add("hidden"));
            category.querySelectorAll(".color-selector").forEach(el => {
              el.classList.add("hidden");
              el.innerHTML = "";
            });
            category.querySelectorAll(".bundle-products").forEach(el => el.classList.add("hidden"));
            if (styleNote) styleNote.classList.add("hidden");
            if (styleNoteText) styleNoteText.textContent = "";
          });
          styleNoteContinue._bound = true;
        }

        // reset view inside this category
        category.querySelectorAll(".bundle-products").forEach(p => p.classList.add("hidden"));
        productsGroup.classList.add("hidden");
        sizeSelector.classList.remove("hidden");
        sizeChart.classList.remove("hidden");
        colorSelector.classList.add("hidden");
        colorSelector.innerHTML = "";
        if (sizeLabel) sizeLabel.textContent = "";  // reset label
        if (colorLabel) colorLabel.textContent = ""; // reset label
        category.querySelectorAll(".size-btn").forEach(b => b.classList.remove("is-active"));
        category.querySelectorAll(".color-btn").forEach(b => b.classList.remove("is-active"));
        productsGroup.querySelectorAll(".bundle-product").forEach(prod => prod.classList.add("hidden"));

        // show size selector
        sizeSelector.classList.remove("hidden");
        sizeChart.classList.remove("hidden");
        colorSelector.classList.add("hidden");
        colorSelector.innerHTML = "";

        // reset labels
        if (sizeLabel) sizeLabel.textContent = "";
        if (colorLabel) colorLabel.textContent = "";
        category.querySelectorAll(".size-btn").forEach(b => b.classList.remove("is-active"));
        category.querySelectorAll(".color-btn").forEach(b => b.classList.remove("is-active"));
        productsGroup.querySelectorAll(".bundle-product").forEach(prod => prod.classList.add("hidden"));

        /* ------------------------------
           🔥 Build dynamic sizes here
        --------------------------------*/
        const sizeLists = sizeSelector.querySelector(".size-lists");

        // get all sizes from this collection's products
        const allSizes = Array.from(productsGroup.querySelectorAll(".bundle-product"))
          .map(prod => prod.dataset.size)
          .filter(Boolean);

        // ✅ dedupe sizes case-insensitively
        const uniqueSizes = Array.from(
          allSizes.reduce((map, size) => {
            const trimmed = size.trim();
            if (!trimmed) return map;
            const key = trimmed.toLowerCase();
            if (!map.has(key)) map.set(key, trimmed);
            return map;
          }, new Map()).values()
        );
        const sizeOrder = ["xxs", "xs", "s", "m", "l", "xl", "xxl", "xxxl"];
        const sortedSizes = uniqueSizes.slice().sort((a, b) => {
          const aIndex = sizeOrder.indexOf(a.toLowerCase());
          const bIndex = sizeOrder.indexOf(b.toLowerCase());
          if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
        // rebuild size buttons
        sizeLists.innerHTML = sortedSizes.map(size =>
          `<button class="size-btn" data-size="${size}">${size}</button>`
        ).join("");

        // bind click events for new buttons
        sizeLists.querySelectorAll(".size-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            sizeLists.querySelectorAll(".size-btn").forEach(b => b.classList.remove("is-active"));
            btn.classList.add("is-active");

            const selectedSize = btn.dataset.size;
            if (sizeLabel) sizeLabel.textContent = selectedSize;
            if (colorLabel) colorLabel.textContent = "";
          });
        });

        /* ------------------------------
          🔥 Build dynamic sizes here Over
       --------------------------------*/

        // filter size buttons that are valid (must have products with a color)
        sizeSelector.querySelectorAll(".size-btn").forEach((btn) => {
          const size = btn.dataset.size;
          const matching = Array.from(productsGroup.querySelectorAll(`.bundle-product[data-size="${size}"]`));
          const hasValidColor = matching.some(prod => {
            const c = (prod.dataset.color || "").trim().toLowerCase();
            return c && c !== "null";
          });
          btn.style.display = hasValidColor ? "inline-block" : "none";
        });

        // rebuild size selector to avoid duplicate events
        const newSizeSelector = sizeSelector.cloneNode(true);
        sizeSelector.parentNode.replaceChild(newSizeSelector, sizeSelector);

        newSizeSelector.querySelectorAll(".size-btn").forEach((btn) => {
          btn.addEventListener("click", () => {
            // reset size active
            newSizeSelector.querySelectorAll(".size-btn").forEach(b => b.classList.remove("is-active"));
            btn.classList.add("is-active");

            // const scrollItem = btn.closest(".size-btn");

            // scrollItem.scrollIntoView({
            //     behavior: "smooth",
            //     block: "center",
            //     inline: "center"
            // });

            const scrollItem = btn.closest(".size-btn");
            requestAnimationFrame(() => {
              const rect = scrollItem.getBoundingClientRect();
              const absoluteY = window.scrollY + rect.top;
              const offset = 0;
              window.scrollTo({
                top: absoluteY - (window.innerHeight / 2) + offset,
                behavior: "smooth"
              });
            });

            const selectedSize = btn.dataset.size;

            const sizeLabelWrap = category.querySelector(".collection-size");
            const sizeLabel = category.querySelector("[data-role='selected-size']");
            if (sizeLabel) {
              sizeLabel.textContent = selectedSize;
            }

            const colorLabel = category.querySelector("[data-role='selected-color']");
            if (colorLabel) colorLabel.textContent = "";

            const bundleType = this.closest(".bundle-type-content")?.getAttribute("bundle-type");

            if (sizeLabelWrap && bundleType === "undies") {
              sizeLabelWrap.classList.remove("hidden"); // make sure it's visible
            }

            if (sizeLabel) sizeLabel.textContent = selectedSize; // ✅ update label

            // build available colors for this size
            const colorMap = new Map(); // color → { swatchImage, colorTag }
            productsGroup
              .querySelectorAll(`.bundle-product[data-size="${selectedSize}"]`)
              .forEach(prod => {
                const color = (prod.dataset.color || "").trim();
                const swatch = (prod.dataset.swatchImage || "").trim();
                const colorTag = (prod.dataset.colorTag || "").trim();
                if (color && color.toLowerCase() !== "null") {
                  if (!colorMap.has(color)) {
                    colorMap.set(color, { swatch, colorTag });
                  } else if (colorTag) {
                    const entry = colorMap.get(color);
                    if (!entry.colorTag) entry.colorTag = colorTag;
                  }
                }
              });

            if (colorMap.size) {
              colorSelector.innerHTML = [...colorMap.entries()].map(([color, { swatch, colorTag }]) => {
                let style = "";

                if (swatch.startsWith("#") || swatch.startsWith("rgb")) {
                  // Direct color value
                  style = `background:${swatch};background-size:cover;`;
                }
                else if (swatch.startsWith("url(")) {
                  // Already wrapped in url(...)
                  style = `background-image:${swatch};background-size:cover;`;
                }
                else if (swatch.startsWith("http") || swatch.startsWith("//")) {
                  // Raw image link
                  style = `background-image:url('${swatch}');background-size:cover;`;
                }
                else {
                  // Fallback: use color name
                  style = `background:${color};background-size:cover;`;
                }

                const safeColor = escapeHtml(color);
                const safeTag = escapeHtml(colorTag);
                const tagAttr = safeTag ? ` data-color-tag="${safeTag}"` : "";
                const tagMarkup = safeTag ? `<span class="color-tag">${safeTag}</span>` : "";
                return `<button class="color-btn" data-color="${safeColor}"${tagAttr} style="${style}">${tagMarkup}</button>`;
                
              }).join("");

              colorSelector.classList.remove("hidden");
            } else {
              colorSelector.innerHTML = "";
              colorSelector.classList.add("hidden");
            }


            productsGroup.classList.add("hidden");

            // clean old color handler
            if (colorSelector._colorHandler) {
              colorSelector.removeEventListener("click", colorSelector._colorHandler);
            }

            // color click delegation
            colorSelector._colorHandler = (e) => {
              const cBtn = e.target.closest(".color-btn");
              if (!cBtn) return;

              colorSelector.querySelectorAll(".color-btn").forEach(b => b.classList.remove("is-active"));
              cBtn.classList.add("is-active");

              //const scrollItem = btn.closest(".size-btn");

              // cBtn.scrollIntoView({
              //     behavior: "smooth",
              //     block: "center",
              //     inline: "center"
              // });

              // const rect = cBtn.getBoundingClientRect();
              // const absoluteY = window.scrollY + rect.top;
              // const offset = 100;

              // window.scrollTo({
              //   top: absoluteY - (window.innerHeight / 2) + offset, 
              //   behavior: "smooth"
              // });

              requestAnimationFrame(() => {
                const rect = cBtn.getBoundingClientRect();
                const absoluteY = window.scrollY + rect.top;
                const offset = 250;
                window.scrollTo({
                  top: absoluteY - (window.innerHeight / 2) + offset,
                  behavior: "smooth"
                });
              });
              
              const selectedColor = cBtn.dataset.color;
              if (colorLabel) colorLabel.textContent = selectedColor; // ✅ update label

              // show products for size + color
              productsGroup.classList.remove("hidden");

              const shownColors = new Set();

              productsGroup.querySelectorAll(".bundle-product").forEach(prod => {
                const match = prod.dataset.size === selectedSize && prod.dataset.color === selectedColor;
                prod.classList.toggle("hidden", !match);

                if (match) {

                 if (!shownColors.has(selectedColor)) {
                    prod.classList.remove("hidden");
                    shownColors.add(selectedColor);
                  } else {
                    prod.classList.add("hidden");
                  }


                  const addBtn = prod.querySelector("button");
                  if (!addBtn._bound) {
                    const bundleHandler = () => {
                      const productData = {
                        title: prod.querySelector("h5")?.innerText || "",
                        size: prod.dataset.size,
                        color: prod.dataset.color,
                        price: parseFloat(prod.dataset.price),
                        image: prod.querySelector("img")?.src,
                        variantId: prod.dataset.variant,
                        collection: prod.dataset.collection
                      };
                      const categoryEl = prod.closest("[bundle-category]");
                      this.addToBundle(productData, { categoryEl });
                    };

                    addBtn._bundleHandler = bundleHandler;
                    addBtn.addEventListener("click", bundleHandler);
                    addBtn._bound = true;
                  }
                }
              });
            };

            colorSelector.addEventListener("click", colorSelector._colorHandler);
          });
        });

        // auto-select size if only one option AND bundle-type is socks
        const bundleType = this.closest(".bundle-type-content")?.getAttribute("bundle-type");
        const sizeBtns = newSizeSelector.querySelectorAll(".size-btn");

        if (bundleType === "socks" && sizeBtns.length === 1) {
          const onlySizeBtn = sizeBtns[0];
          onlySizeBtn.classList.add("is-active");
          const selectedSize = onlySizeBtn.dataset.size;

          // update label
          const sizeLabel = category.querySelector("[data-role='selected-size']");
          if (sizeLabel) sizeLabel.textContent = selectedSize;

          // ✅ hide size UI
          category.querySelector(".collection-size")?.classList.add("hidden");

          category.querySelector(".size-selector")?.classList.add("hidden");

          // trigger color build logic for this size
          onlySizeBtn.click();
        }

      });
    });
  }

  sizeChart() {
    document.querySelectorAll(".bundle-size-chart").forEach((res) => {
      res.addEventListener("click", (e) => {
        const category = e.currentTarget.closest("[bundle-category]");
        const activeItem = category?.querySelector(".bundle-collection-item.active");
        const handle = activeItem?.dataset.collectionHandle;
        this.initSizeChartParameters()
        if (!handle) return;

        const modal = document.querySelector(
          `.size-chart-wrapper[data-collection-handle="${handle.toLowerCase()}"]`
        );

        if (modal) {
          // close any open modals first
          document.querySelectorAll(".size-chart-wrapper.open").forEach((m) =>
            m.classList.remove("open")
          );
          modal.classList.add("open");
          document.body.classList.add("overflow-hidden");
        }
      });
    });

    // global outside click
    document.addEventListener("click", (e) => {
      const openModal = document.querySelector(".size-chart-wrapper.open");
      if (
        openModal &&
        !openModal.querySelector(".size-chart-modal").contains(e.target) && // not inside modal
        !e.target.closest(".bundle-size-chart") // not the trigger button
      ) {
        openModal.classList.remove("open");
        document.body.classList.remove("overflow-hidden");
      }
    });

    // esc key close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        const openModal = document.querySelector(".size-chart-wrapper.open");
        if (openModal) {
          openModal.classList.remove("open");
          document.body.classList.remove("overflow-hidden");
        }
      }
    });

    // close button inside modal
    document.querySelectorAll("[bundle-size-chart-close-btn]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const modal = btn.closest(".size-chart-wrapper");
        modal?.classList.remove("open");
        document.body.classList.remove("overflow-hidden");
      });
    });
  }
  initSizeChartParameters() {
    // Find the currently open size chart modal inside this bundle-builder
    const wrapper = this.querySelector(".size-chart-wrapper.bundle-builder.open");
    if (!wrapper) return;

    const tabButtons = wrapper.querySelectorAll(".size-chart_tabs-text");

    let isCmActive = false; 
    let isInchActive = true; // assume starts with inches

    function removeTrailingZeros(value) {
      return value.split('.')[0];
    }

    // Ensure each element has a data-inch attribute as original value
    const elements = wrapper.querySelectorAll(".size-chart-values");
    elements.forEach((element) => {
      if (!element.dataset.inch) {
        element.dataset.inch = element.textContent.trim(); // store original inch value
      }
    });

    const convertToCm = () => {
      elements.forEach((element) => {
        const inchValueText = element.dataset.inch; // always read from original inches
        const values = inchValueText.split('-');

        if (values.length === 2) {
          const minInch = parseFloat(values[0].trim());
          const maxInch = parseFloat(values[1].trim());

          if (!isNaN(minInch) && !isNaN(maxInch)) {
            const minCm = Math.round(minInch * 2.54);
            const maxCm = Math.round(maxInch * 2.54);
            element.textContent = removeTrailingZeros(minCm.toString()) + "-" + removeTrailingZeros(maxCm.toString());
          }
        } else {
          const inch = parseFloat(inchValueText);
          if (!isNaN(inch)) {
            const cm = Math.round(inch * 2.54);
            element.textContent = removeTrailingZeros(cm.toString());
          }
        }
      });

      isCmActive = true;
      isInchActive = false;
    };

    const convertToInch = () => {
      elements.forEach((element) => {
        const inchValueText = element.dataset.inch; // restore from original
        element.textContent = inchValueText;
      });

      isInchActive = true;
      isCmActive = false;
    };

    if (tabButtons.length) {
      tabButtons.forEach((btn) => {
        btn.addEventListener("click", () => {
          const unit = btn.dataset.unit; // expects data-unit="in" or "cm"

          if (unit === "cm" && !isCmActive) {
            convertToCm();
            tabButtons.forEach(b => b.classList.remove("active"));
            btn.classList.add("active");
          }

          if (unit === "in" && !isInchActive) {
            convertToInch();
            tabButtons.forEach(b => b.classList.remove("active"));
            btn.classList.add("active");
          }
        });
      });
    }
  }

  addToBundle(productData, { categoryEl } = {}) {
    if (this.bundleItems.length >= this.maxSlots) return;

    this.bundleItems.push(productData);

    this.lockActiveStyleAndCategory(categoryEl);

    // If we just filled the bundle, defer the render to the next tick
    // so the click that added the last item can't immediately hit the new ATC handler.
    const justFilled = this.bundleItems.length >= this.maxSlots;
    if (justFilled) {
      setTimeout(() => this.renderBundleBox(), 0);
    } else {
      this.renderBundleBox();
    }
  }


  removeFromBundle(index) {
    this.bundleItems.splice(index, 1);
    this.renderBundleBox();
    this.unlockStylesAndCategories();
  }

  lockActiveStyleAndCategory(categoryEl) {
    if (!this.bundleItems.length) return;

    const activeStyle = categoryEl?.querySelector(".bundle-collection-item.active")
      || Array.from(this.querySelectorAll(".bundle-collection-item.active")).pop()
      || this.querySelector(".bundle-collection-item.active");
    if (activeStyle) {
      this.querySelectorAll(".bundle-collection-item").forEach(item => {
        if (item.classList.contains("active")) {
          item.classList.remove("is-disabled");
          item.removeAttribute("aria-disabled");
        } 
      });

      const category = categoryEl || activeStyle.closest("[bundle-category]");
      const styleNote = category?.querySelector("[data-role='style-note']");
      const styleNoteText = category?.querySelector("[data-role='style-note-text']");
      if (styleNoteText) {
        const collectionName = activeStyle.dataset.collectionTitle || activeStyle.querySelector("h4")?.innerText || "";
        const safeCollectionName = escapeHtml(collectionName);
        styleNoteText.innerHTML = `<span class="selected-style">${safeCollectionName} Selected</span> - Changing style will clear selection.`;
      }
      if (styleNote) {
        styleNote.classList.remove("hidden");
      }
    }

    const activeCategory = this.querySelector(".collection-category-item.is-active");
    if (activeCategory) {
      this.querySelectorAll(".collection-category-item").forEach(tab => {
        if (tab === activeCategory) {
          tab.classList.remove("is-disabled");
          tab.removeAttribute("aria-disabled");
        }
      });
    }
  }

  unlockStylesAndCategories() {
    if (this.bundleItems.length === 0) {
      this.querySelectorAll(".bundle-collection-item").forEach(item => {
        item.classList.remove("is-disabled");
        item.removeAttribute("aria-disabled");
      });
      this.querySelectorAll(".collection-category-item").forEach(tab => {
        tab.classList.remove("is-disabled");
        tab.removeAttribute("aria-disabled");
      });
      this.querySelectorAll("[data-role='style-note']").forEach(noteEl => {
        noteEl.classList.add("hidden");
        const noteText = noteEl.querySelector("[data-role='style-note-text']");
        if (noteText) noteText.textContent = "";
      });
    }
  }

  windowHistory() {
    window.addEventListener("popstate", (index) => {

      const bundleContents = document.querySelectorAll(".bundle-type-content");
      const bundleMains = document.querySelectorAll(".bundle-builder-main");
      const bundleMainContainer = document.querySelector(".bundle-main-container");
      const bundleCloseBtn = document.querySelector(".builder-close-btn");
      const backBtn = document.querySelector(".bundle-back-btn")

      // Reset all bundle mains and return to landing screen
      bundleMains.forEach(main => main.classList.add("hidden"));
      const landing = document.querySelector('.bundle-landing');
      landing?.classList.remove('hidden');
      // document.body.classList.add('on-landing');
      document.body.classList.remove('on-landing');
      bundleMainContainer?.classList.add('hidden');
      bundleCloseBtn?.classList.add('hidden');
      backBtn?.classList.add('hidden')

      // Reset all bundle contents (media + heading visible again)
      bundleContents.forEach(content => {
        content.classList.remove("hidden");
        const media = content.querySelector(".bundle-type-media");
        const heading = content.querySelector(".bundle-type-heading, .bundle-type-media span");
        if (media) media.classList.remove("hidden");
        if (heading) heading.classList.remove("hidden");
      });

      // ---- Reset categories, sizes, colors ----
      document.querySelectorAll(".collection-category-item").forEach(h => h.classList.remove("is-active"));
      // document.querySelectorAll("[bundle-category]").forEach(c => c.classList.add("hidden"));
      document.querySelectorAll(".bundle-collection-list").forEach(c => c.classList.add("hidden"));

      // reset selected labels
      document.querySelectorAll("[data-role='selected-collection']").forEach(el => el.textContent = "");
      document.querySelectorAll("[data-role='selected-size']").forEach(el => el.textContent = "");
      document.querySelectorAll("[data-role='selected-color']").forEach(el => el.textContent = "");

      // reset buttons
      document.querySelectorAll(".bundle-collection-item").forEach(el => {
        el.classList.remove("active", "is-disabled");
        el.removeAttribute("aria-disabled");
      });
      document.querySelectorAll("[data-role='style-note']").forEach(noteEl => {
        noteEl.classList.add("hidden");
        const noteText = noteEl.querySelector("[data-role='style-note-text']");
        if (noteText) noteText.textContent = "";
      });
      document.querySelectorAll(".size-btn").forEach(el => el.classList.remove("is-active"));
      document.querySelectorAll(".color-btn").forEach(el => el.classList.remove("is-active"));

      // reset selectors
      document.querySelectorAll(".size-selector").forEach(el => el.classList.add("hidden"));
      document.querySelectorAll(".bundle-size-chart").forEach(el => el.classList.add("hidden"));
      document.querySelectorAll(".color-selector").forEach(el => {
        el.classList.add("hidden");
        el.innerHTML = ""; // clear dynamic colors
      });

      // reset product visibility
      document.querySelectorAll(".bundle-product").forEach(el => el.classList.add("hidden"));

      this.initTabs()
      this.resetBuilder();
    });
  }

  renderBundleBox() {
    const slotsContainers = this.querySelectorAll("[data-role='bundle-slots']");
    const subtotalEls = this.querySelectorAll("[data-role='subtotal']");
    const savingsEls = this.querySelectorAll("[data-role='savings']");
    const progressEls = this.querySelectorAll("[data-role='progress']");
    const checkoutBtns = this.querySelectorAll(".bundle-checkout");
    const mainBox = this.querySelector(".bundle-main-box");
    const mobileBox = this.querySelector(".main-box-container.mobile");


    if (mainBox) {
      mainBox.classList.toggle("filled", this.bundleItems.length > 0);
    }
    if (mobileBox) {
      mobileBox.classList.toggle("filled", this.bundleItems.length > 0);
    }

    // ---- render slots for both desktop & mobile ----
    slotsContainers.forEach((slotsContainer) => {
      slotsContainer.innerHTML = "";
      for (let i = 0; i < this.maxSlots; i++) {
        if (this.bundleItems[i]) {
          const item = this.bundleItems[i];
          slotsContainer.innerHTML += `
            <div class="bundle-slot filled">
              <img src="${item.image}" alt="${item.title}">
              <div class="bundle-filled-data">
                <div class="bundle-filled-info">
                  <h5>${item.collection} - ${item.color}</h5>
                  <div class="bundle-item-size">Size: <span>${item.size}</span></div>
                </div>
                <button data-remove="${i}">Remove</button>
              </div>
            </div>`;
        } else {
          slotsContainer.innerHTML += `
            <div class="bundle-slot empty">
              <div class="empty-image">
                <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.5" y="0.5" width="71" height="71" rx="4.5" stroke="black" stroke-dasharray="5 5"></rect>
                </svg>
              </div>
              <span>Add a Pair</span>
            </div>`;
        }
      }
    });

    // ---- totals ----
    const originalTotal = this.bundleItems.reduce((sum, p) => sum + p.price, 0);
    const savingsPrice = Number(
      (originalTotal * this.bundleDiscountPercent / 100).toFixed(2)
    );

    this.bundlePrice = Number(
      (originalTotal - savingsPrice).toFixed(2)
    );
    let subtotalText = "--";
    if (this.bundleItems.length === 0) {
      subtotalText = "--";
    } else if (this.bundleItems.length < this.maxSlots) {
      subtotalText = `${window?.currencySymbol || ""}${originalTotal}`;
    } else if (this.bundlePrice > 0) {
      if (originalTotal > this.bundlePrice) {
        // Only show strike price if discounted
        subtotalText =
          `${window?.currencySymbol || ""}${this.bundlePrice} ` +
          `<s>${window?.currencySymbol || ""}${originalTotal}</s>`;
      } else {
        // No discount → just show the bundle price
        subtotalText = `${window?.currencySymbol || ""}${this.bundlePrice}`;
      }
    }


    subtotalEls.forEach(el => (el.innerHTML = subtotalText));

    savingsEls.forEach(el => {
      if (this.bundlePrice > 0) {
        const savings = savingsPrice;
        el.textContent =
          savings > 0
            ? `${window.currencySymbol || ""}${savings}`
            : `--`;
      } else {
        el.textContent = "--";
      }
    });

    // ---- progress ----
    progressEls.forEach(el => (el.textContent = this.bundleItems.length));

    // ---- checkout buttons ----
    checkoutBtns.forEach(btn => {
      btn.disabled = this.bundleItems.length < this.maxSlots;

      if (this.bundleItems.length < this.maxSlots) {
        btn.textContent = `${this.bundleItems.length}/${this.maxSlots} SELECTED • SELECT A PRODUCT`;
      } else {
        btn.innerHTML = `ADD TO CART ${subtotalText}`;
      }

      // btn.textContent =
      //   this.bundleItems.length < this.maxSlots
      //     ? `${this.bundleItems.length}/${this.maxSlots} SELECTED • SELECT A PRODUCT`
      //     : `ADD TO CART ${subtotalText}`;

      if (!btn._hasClick) {
        btn.addEventListener("click", () => this.addBundleToCart());
        btn._hasClick = true; // prevent duplicate binding
      }
    });

    // ---- wire up remove buttons (both desktop + mobile) ----
    this.querySelectorAll("[data-remove]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = parseInt(btn.dataset.remove, 10);
        this.removeFromBundle(idx);
      });
    });

    // ---- disable add buttons when full ----
    // ---- handle add buttons when bundle is full ----
    const isFull = this.bundleItems.length >= this.maxSlots;
    this.querySelectorAll(".bundle-product button").forEach((btn) => {
      const productEl = btn.closest(".bundle-product");
      const productPrice = Number(productEl?.dataset.price);
      const formattedProductPrice = Number.isFinite(productPrice)
        ? this.formatBundlePrice(productPrice, { includeSuffix: false })
        : null;

      const baseLabel = formattedProductPrice
        ? `Add to bundle - ${formattedProductPrice}`
        : "Add to bundle";

      const parts = [];
      if (Number.isFinite(this.maxSlots) && this.maxSlots > 0) {
        parts.push(`${this.bundleItems.length}/${this.maxSlots}`);
      }
      parts.push(baseLabel);
      const buttonLabel = parts.join(" ");

      const isMobile = window.innerWidth <= 560;

      // 🔧 Always remove any previous ATC handler first
      if (btn._atcHandler) {
        btn.removeEventListener("click", btn._atcHandler);
        btn._atcHandler = null;
      }

      if (isFull) {
        if (isMobile) {
          btn.disabled = false;
          btn.classList.remove("disabled");
          btn.innerHTML = `ADD TO CART ${subtotalText}`;

          // 🔧 Also block the "add to bundle" handler while full
          if (btn._bundleHandler) {
            btn.removeEventListener("click", btn._bundleHandler);
          }

          // Rebind as ATC (store the reference so we can remove it later)
          btn._atcHandler = (e) => this.addBundleToCart(e);
          btn.addEventListener("click", btn._atcHandler);
        } else {
          btn.disabled = true;
          btn.classList.add("disabled");
          btn.textContent = buttonLabel;
        }
      } else {
        // Not full → restore "Add to bundle" mode
        btn.disabled = false;
        btn.classList.remove("disabled");
        btn.textContent = buttonLabel;

        // 🔧 Put the bundle handler back if we have one
        if (btn._bundleHandler) {
          btn.addEventListener("click", btn._bundleHandler);
        }
      }
    });



    // also update toggleBtn progress text
    const toggleBtn = this.querySelector(".bundle-toggle-btn");
    if (toggleBtn) {
      toggleBtn.querySelector("[data-role='progress']").textContent = this.bundleItems.length;
      toggleBtn.innerHTML = `
    <span data-role="progress">${this.bundleItems.length}</span>/${this.maxSlots}
    SELECTED • VIEW BUNDLE 
    <span class="chevron">
      <svg width="15" height="10" viewBox="0 0 15 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 9L7.5 2L14 9" stroke="black" stroke-width="2"></path>
      </svg>
    </span>
  `;
    }
  }

  resetBuilder() {
    this.bundleItems = [];
    this.restoreBundleOptionDefaults();
    this.selectDefaultBundleOptions();
    this.renderBundleBox();

    this.querySelectorAll(".bundle-collection-item").forEach(item => {
      item.classList.remove("active", "is-disabled");
      item.removeAttribute("aria-disabled");
    });
    this.querySelectorAll("[data-role='style-note']").forEach(noteEl => {
      noteEl.classList.add("hidden");
      const noteText = noteEl.querySelector("[data-role='style-note-text']");
      if (noteText) noteText.textContent = "";
    });
    this.querySelectorAll(".collection-category-item").forEach(tab => {
      tab.classList.remove("is-disabled");
      tab.removeAttribute("aria-disabled");
    });
  }

  async addBundleToCart(event) {
    
    if (this.bundleItems.length < this.maxSlots) return;

    const checkoutBtns = this.querySelectorAll(".bundle-checkout");
    checkoutBtns.forEach(btn => btn.classList.add("loading"));
    if (event) {
       event.target.classList.add("secondary-atc", "loading");
    }

    // Prepare Shopify cart request
    const items = this.bundleItems.map((p) => ({
      id: p.variantId, // variant id required!
      quantity: 1,
      properties: {
        _bundle: "true", // optional: mark items as bundle
        _bundle_discount: this.bundlePrice,
        _bundle_group: Date.now(), // unique group key
      },
    }));

    try {
      const sectionIds = this.#getCartSectionIds();

      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(
          `items[${index}][quantity]`,
          String(item.quantity ?? 1)
        );

        if (item.properties) {
          Object.entries(item.properties).forEach(([key, value]) => {
            formData.append(
              `items[${index}][properties][${key}]`,
              String(value)
            );
          });
        }
      });

      if (sectionIds.length) {
        formData.append("sections", sectionIds.join(","));
      }
      formData.append("sections_url", window.location.pathname);

      const fetchCfg = fetchConfig("javascript", { body: formData });
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: "text/html",
        },
      });

      const response = await res.json();
      if (!res.ok || response.status) {
        const message =
          response?.message ||
          response?.description ||
          "Failed to add bundle to cart. Please try again.";
        throw new Error(message);
      }

      const sections = await this.#buildSectionsPayload(sectionIds, response?.sections);

      const cartRes = await fetch("/cart.js", { cache: 'no-store' });
      const cartData = await cartRes.json();
      const itemCount = Number(cartData?.item_count ?? 0);

      this.#emitCartUpdate({
        sectionIds,
        sections,
        productId: items[0]?.id,
        itemCount,
      });
    } catch (err) {
      console.error("Bundle ATC failed", err);
      alert(err?.message || "Failed to add bundle to cart. Please try again.");
    }
    finally {
      checkoutBtns.forEach((btn) => btn.classList.remove("loading"));
      if (event) {
        event.target.classList.remove("secondary-atc", "loading");
      }
      this.closeDialog();
    }

  }
  #getCartSectionIds() {
    return Array.from(
      document.querySelectorAll("cart-items-component[data-section-id]")
    )
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  async #buildSectionsPayload(sectionIds, sections = {}) {
    const payload = sections ? { ...sections } : {};
    const missing = sectionIds.filter((id) => id && !payload[id]);

    if (!missing.length) return payload;

    await Promise.all(
      missing.map(async (sectionId) => {
        try {
          payload[sectionId] = await sectionRenderer.getSectionHTML(sectionId, false);
        } catch (error) {
          console.warn(`Failed to render section ${sectionId}`, error);
        }
      })
    );

    return payload;
  }

  #emitCartUpdate({ sectionIds, sections, productId, itemCount }) {
    const primarySectionId = sectionIds[0] || "";
    this.dispatchEvent(
      new CartAddEvent({}, primarySectionId, {
        didError: false,
        source: "bundle-builder",
        itemCount,
        productId,
        sections,
      })
    );

    this.#openCartDrawer();
  }

  #openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer?.hasAttribute('auto-open')) {
      cartDrawer.open?.();
    }
  }
}

customElements.define("bundle-builder", BundleBuilder);


document.addEventListener("DOMContentLoaded", function () {
  var ua = navigator.userAgent.toLowerCase();
  var html = document.documentElement;

  // ✅ Device detection
  if (/android/i.test(ua)) {
    html.classList.add("device-android");
  } else if (/iphone|ipad|ipod/.test(ua)) {
    html.classList.add("device-ios");
  } else {
    html.classList.add("device-unknown");
  }

  // ✅ Browser detection
  if (ua.indexOf("chrome") > -1 && ua.indexOf("edg") === -1 && ua.indexOf("opr") === -1) {
    html.classList.add("browser-chrome");
  } else if (ua.indexOf("safari") > -1 && ua.indexOf("chrome") === -1) {
    html.classList.add("browser-safari");
  } else if (ua.indexOf("firefox") > -1) {
    html.classList.add("browser-firefox");
  } else if (ua.indexOf("edg") > -1) {
    html.classList.add("browser-edge");
  } else if (ua.indexOf("opr") > -1 || ua.indexOf("opera") > -1) {
    html.classList.add("browser-opera");
  } else {
    html.classList.add("browser-unknown");
  }
});

document.addEventListener("DOMContentLoaded", () => {
  // Show footer on landing by default for bundle-builder
  // document.body.classList.add('on-landing');

  // Hide socks option entirely
  document.querySelectorAll('.bundle-type-content[bundle-type="socks"]').forEach(el => {
    el.classList.add('hidden');
  });

  // Landing -> open Undies directly
  const landing = document.querySelector('.bundle-landing');
  const continueBtn = document.querySelector('.bundle-continue-btn');
  const bundleMainContainerEl = document.querySelector('.bundle-main-container');
  const bundleCloseBtn = document.querySelector('.builder-close-btn');

  if (continueBtn) {
    continueBtn.addEventListener('click', () => {
      // Hide landing
      landing?.classList.add('hidden');
      // Hide footer after entering builder
      // document.body.classList.remove('on-landing');
      document.body.classList.add('on-landing');

      // Show the container (hidden by default), but don't show the selection grid
      bundleMainContainerEl?.classList.remove('hidden');
      bundleMainContainerEl?.classList.remove('active');
      bundleCloseBtn?.classList.add('hidden');

      const undiesMedia = document.querySelector('.bundle-type-content[bundle-type="undies"] .bundle-type-media');
      const firstMedia = document.querySelector('.bundle-type-content .bundle-type-media');
      (undiesMedia || firstMedia)?.click();
    });
  }

  const bundleContents = document.querySelectorAll(".bundle-type-content");
  const bundleMains = document.querySelectorAll(".bundle-builder-main");
  const backBtn = document.querySelector(".bundle-back-btn")
  bundleContents.forEach((content, index) => {
    const media = content.querySelector(".bundle-type-media");
    const heading = document.querySelector(".bundle-type-heading");

    if (media) {
      media.addEventListener("click", () => {

        const targetMain = bundleMains[index];
        bundleMainContainerEl?.classList.remove("active");
        bundleCloseBtn?.classList.add("hidden");
        backBtn?.classList.remove("hidden")

        // Hide all bundle mains
        bundleMains.forEach(main => main.classList.add("hidden"));
        targetMain.classList.remove("hidden");

        // Hide all contents except clicked
        bundleContents.forEach(c => c.classList.add("hidden"));
        content.classList.remove("hidden");

        // Hide its media + heading
        media.classList.add("hidden");
        if (heading) heading.classList.add("hidden");
        // ✅ Push state with a marker that it's bundle UI
        history.pushState({ bundleIndex: index }, "");
      });
    }
  });

  if (backBtn) {
    backBtn.addEventListener("click", () => {
      history.back(); // ← triggers popstate event
    });
  }

})
